import { delay, delayJitter } from './timers'

export interface RetryOptions {
  /** Maximum number of attempts (including the first). Defaults to 3. */
  maxAttempts?: number
  /** Base delay in ms before the first retry. Defaults to 1000. */
  baseDelay?: number
  /** Maximum delay in ms between retries. Defaults to 30000. */
  maxDelay?: number
  /** Jitter factor (0–1) applied to each delay. Defaults to 0.5. */
  jitter?: number
  /**
   * Classify whether an error is retryable.
   * Return true to retry, false to throw immediately.
   * Defaults to retrying all errors.
   */
  isRetryable?: (
    error: unknown,
    attempt: number
  ) => Promise<boolean>
  /** Called before each retry wait. Useful for logging. */
  onRetry?: (
    error: unknown,
    attempt: number,
    delayMs: number
  ) => void
}

/**
 * Retry an async operation with exponential backoff and jitter.
 *
 * @example
 * const data = await retry(() => fetch('/api/data'), {
 *   maxAttempts: 5,
 *   isRetryable: async (err) => err instanceof TypeError,
 * })
 */
export async function retry<T>(
  fn: (attempt: number) => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxAttempts = 3,
    baseDelay = 50,
    maxDelay = 10_000,
    jitter = 0.5,
    isRetryable = async () => true,
    onRetry,
  } = options

  let lastError: unknown

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn(attempt)
    } catch (error) {
      lastError = error

      if (
        attempt >= maxAttempts ||
        !(await isRetryable(error, attempt))
      ) {
        throw error
      }

      const exponentialDelay = Math.min(
        baseDelay * 2 ** attempt,
        maxDelay
      )
      const delayMs = delayJitter(exponentialDelay, jitter)

      onRetry?.(error, attempt, delayMs)

      await delay(delayMs)
    }
  }

  throw lastError
}
